import type { Screen } from '../../types';
import type {
  PipeexUiWizardProgressSectionsSnapshot,
  PipeexUiWizardProgressSnapshot,
} from './pipeexWizardProfileState';

const KNOWN_WIZARD_SCREENS: readonly Screen[] = [
  'welcome',
  'menu',
  'connectorBinding',
  'quickFillProperties',
  'boltConnection',
  'additionalCategories',
  'processProperties',
  'mappingConfiguration',
  'pcfValidation',
];

const knownWizardScreens = new Set<string>(KNOWN_WIZARD_SCREENS);
const connectorSections = new Set(['connectors', 'bom', 'components']);
const boltSections = new Set(['core', 'supporting']);
const processSections = new Set(['processConditions', 'projectProperties']);
const additionalCategorySections = new Set(['valve', 'supports', 'instrument']);

const normalizeWizardScreen = (raw: unknown, fallback: Screen = 'welcome'): Screen =>
  typeof raw === 'string' && knownWizardScreens.has(raw) ? (raw as Screen) : fallback;

const normalizeWizardNavigationHistory = (raw: unknown, screen: Screen): Screen[] => {
  const history = Array.isArray(raw)
    ? raw.filter((entry): entry is Screen =>
        typeof entry === 'string' && knownWizardScreens.has(entry)
      )
    : [];

  const distinctHistory = Array.from(new Set(history));
  if (!distinctHistory.includes(screen)) {
    distinctHistory.push(screen);
  }

  return distinctHistory.length > 0 ? distinctHistory : [screen];
};

const normalizeWizardProgressSections = (
  raw: unknown
): PipeexUiWizardProgressSectionsSnapshot => {
  const sections = raw && typeof raw === 'object'
    ? raw as Partial<Record<keyof PipeexUiWizardProgressSectionsSnapshot, unknown>>
    : {};

  return {
    connectorBinding: connectorSections.has(String(sections.connectorBinding))
      ? sections.connectorBinding as PipeexUiWizardProgressSectionsSnapshot['connectorBinding']
      : 'connectors',
    boltConnection: boltSections.has(String(sections.boltConnection))
      ? sections.boltConnection as PipeexUiWizardProgressSectionsSnapshot['boltConnection']
      : 'core',
    processProperties: processSections.has(String(sections.processProperties))
      ? sections.processProperties as PipeexUiWizardProgressSectionsSnapshot['processProperties']
      : 'processConditions',
    additionalCategories: additionalCategorySections.has(String(sections.additionalCategories))
      ? sections.additionalCategories as PipeexUiWizardProgressSectionsSnapshot['additionalCategories']
      : 'valve',
  };
};

export function normalizePipeexUiWizardProgressSnapshot(
  raw: unknown
): PipeexUiWizardProgressSnapshot | undefined {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }

  const progress = raw as {
    screen?: unknown;
    navigationHistory?: unknown;
    sections?: unknown;
    hasSavedOnce?: unknown;
    isWorkflowApplied?: unknown;
  };
  const screen = normalizeWizardScreen(progress.screen);

  return {
    screen,
    navigationHistory: normalizeWizardNavigationHistory(progress.navigationHistory, screen),
    sections: normalizeWizardProgressSections(progress.sections),
    hasSavedOnce: progress.hasSavedOnce === true,
    isWorkflowApplied: progress.isWorkflowApplied === true,
  };
}
